import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Menu,
  X,
  Brain,
  User,
  ChevronDown,
  Target,
  Users,
  Settings,
  FileText
} from 'lucide-react';
import { getUser, clearAuth, isPremiumUser } from '../auth';

const HeaderContainer = styled.header`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: #1f2937;
  font-weight: 700;
  font-size: 1.5rem;

  &:hover {
    color: #6366f1;
  }
`;

const LogoIcon = styled.div`
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: nowrap;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: ${(props) => (props.isOpen ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    padding: 1rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border-radius: 0 0 10px 10px;
    white-space: normal;
    gap: 1rem;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: ${(props) => (props.active ? '#6366f1' : '#6b7280')};
  font-weight: 500;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  padding: 0.5rem 0;
  font-size: 1rem;

  &:hover {
    color: #6366f1;
  }
`;

const CTAButton = styled(Link)`
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    margin-left: 0;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => (props.active ? '#6366f1' : '#6b7280')};
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0;
  transition: color 0.2s;
  font-size: 1rem;
  white-space: nowrap;

  &:hover {
    color: #6366f1;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 0.5rem 0;
  min-width: 200px;
  z-index: 1000;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
  transform: ${(props) =>
    props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: #4a5568;
  text-decoration: none;
  transition: all 0.2s;
  font-size: 0.9rem;

  &:hover {
    background: #f7fafc;
    color: #6366f1;
  }
`;

const DropdownIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
`;

const LogoutButton = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  color: #6b7280;
  padding: 0.55rem 1rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  margin-left: 0.5rem;

  &:hover {
    background: #f9fafb;
    color: #374151;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleDropdownToggle = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  useEffect(() => {
    const user = getUser();
    setCurrentUser(user);
    setIsPremium(isPremiumUser(user));
  }, [location.pathname]);

  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const handleLogout = () => {
    clearAuth();
    setCurrentUser(null);
    setIsPremium(false);
    navigate('/');
  };

  const profileLabel = currentUser?.name || currentUser?.email || 'Profilim';

  return (
    <HeaderContainer>
      <Nav>
        <Logo to="/">
          <LogoIcon>
            <Brain size={24} />
          </LogoIcon>
          İşHazır
        </Logo>

        <NavLinks isOpen={isMenuOpen}>
          <NavLink to="/" active={location.pathname === '/'}>
            Ana Sayfa
          </NavLink>

          <DropdownContainer data-dropdown>
            <DropdownButton
              active={['/cv-analyzer', '/cv-generator', '/job-match'].includes(
                location.pathname
              )}
              onClick={() => handleDropdownToggle('cv')}
            >
              CV Araçları
              <ChevronDown size={16} />
            </DropdownButton>
            <DropdownMenu isOpen={activeDropdown === 'cv'}>
              <DropdownItem to="/cv-analyzer">
                <DropdownIcon>
                  <User size={16} />
                </DropdownIcon>
                CV Analizi
              </DropdownItem>

              <DropdownItem to="/cv-generator">
                <DropdownIcon>
                  <FileText size={16} />
                </DropdownIcon>
                ATS Uyumlu CV Oluştur
              </DropdownItem>

              <DropdownItem to="/job-match">
                <DropdownIcon>
                  <Target size={16} />
                </DropdownIcon>
                İlan Uyum Analizi
              </DropdownItem>
            </DropdownMenu>
          </DropdownContainer>

          <DropdownContainer data-dropdown>
  <DropdownButton
    active={['/interview-simulator', '/career-coach', '/global-jobs'].includes(
      location.pathname
    )}
    onClick={() => handleDropdownToggle('career')}
  >
    Kariyer
    <ChevronDown size={16} />
  </DropdownButton>
  <DropdownMenu isOpen={activeDropdown === 'career'}>
    <DropdownItem to="/interview-simulator">
      <DropdownIcon>
        <Users size={16} />
      </DropdownIcon>
      Mülakat Simülasyonu
    </DropdownItem>
    <DropdownItem to="/career-coach">
      <DropdownIcon>
        <Settings size={16} />
      </DropdownIcon>
      Kariyer Koçluğu
    </DropdownItem>
    <DropdownItem to="/global-jobs">
      <DropdownIcon>
        <Target size={16} />
      </DropdownIcon>
      Global İş İlanları
    </DropdownItem>
  </DropdownMenu>
</DropdownContainer>


          {currentUser ? (
            <>
              <NavLink
                to="/dashboard"
                active={location.pathname === '/dashboard'}
              >
                <User size={16} />
                {profileLabel}
                {isPremium && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: '0.75rem',
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: '#eef2ff',
                      color: '#4f46e5'
                    }}
                  >
                    Premium
                  </span>
                )}
              </NavLink>
              <LogoutButton type="button" onClick={handleLogout}>
                Çıkış Yap
              </LogoutButton>
            </>
          ) : null}

          {!currentUser && (
            <CTAButton to="/login">
              Giriş Yap
            </CTAButton>
          )}
        </NavLinks>

        <MobileMenuButton onClick={toggleMenu}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </MobileMenuButton>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;

